import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { graphql, compose } from 'react-apollo';
import { push } from 'react-router-redux';
import { omit, get, pickBy } from 'lodash';
import moment from 'moment';
import { createFilterOptions, tabIsActive } from './OfferNav';
import OfferGrid from './OfferGrid';
import { DASHBOARD_OFFER_LIMIT as limit } from '../../config';
import { filteredOptionsQuery } from '../../apollo';

const OfferPagination = (
  {
    loading,
    queryName,
    filter,
    search,
    timing,
    typeOfOffer,
    offerQuery: { items: offers, total },
    changePage,
    pageIndex,
    location,
    match,
    filteredOptionsData,
  },
  { tenant }
) => {
  // TODO: Refactor to separate conditional logic from this component
  let filteredData = offers;
  if (queryName === 'investorOffers') {
    filteredData = offers.reduce((result, o) => {
      if (o.offer.visibility !== 'UNPUBLISHED') {
        result.push({
          ...o.offer,
          investorOffer: omit(o, ['offer']),
        });
      }
      return result;
    }, []);
  }

  if (queryName === 'allUserInvestments') {
    filteredData = offers.map(
      ({
        _id: investmentId,
        createdAt,
        investmentAmount,
        investmentAmountCrypto,
        investmentStatus,
        paymentMethod,
        paymentType,
        sharesOrTokensPurchased,
        offer: { _id: offerId, name: offerName, typeOfOffer },
      }) => ({
        offerName,
        transactionDate: moment.utc(new Date(createdAt)).format('MM/DD/YYYY'),
        investmentAmount,
        investmentAmountCrypto,
        investmentStatus,
        paymentMethod,
        paymentType,
        sharesOrTokensPurchased,
        investmentId,
        offerId,
        typeOfOffer,
      })
    );
  }

  const totalPages = Math.ceil(total / limit);

  const filterOptions = createFilterOptions(tenant);

  const filterTitle = () => {
    const activeFilter = () =>
      Object.keys(filterOptions).find((option) => {
        if (filterOptions[option].to.includes(location.pathname)) {
          return tabIsActive(match, location, option);
        }
        return '';
      });

    return get(filterOptions, [activeFilter(), 'title'], '');
  };

  return (
    <OfferGrid
      filteredOptionsData={filteredOptionsData}
      loading={loading}
      filteredData={filteredData}
      filter={filter}
      filterTitle={filterTitle()}
      search={search}
      timing={timing}
      typeOfOffer={typeOfOffer}
      totalPages={totalPages}
      pageIndex={pageIndex}
      changePage={changePage}
      queryName={queryName}
    />
  );
};

OfferPagination.propTypes = {
  loading: PropTypes.bool.isRequired,
  queryName: PropTypes.string.isRequired,
  offerQuery: PropTypes.object,
  changePage: PropTypes.func.isRequired,
  pageIndex: PropTypes.number.isRequired,
  filter: PropTypes.object,
  search: PropTypes.string,
  timing: PropTypes.string,
  typeOfOffer: PropTypes.string,
  location: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
  filteredOptionsData: PropTypes.object.isRequired,
};

OfferPagination.defaultProps = {
  offerQuery: {
    items: [],
    total: 0,
  },
  filter: null,
  timing: null,
  typeOfOffer: null,
  search: null,
};

OfferPagination.contextTypes = {
  tenant: PropTypes.object,
};

const withPagination = (ComponentToWrap) => (props) => {
  const WithData = compose(
    graphql(filteredOptionsQuery, {
      name: 'filteredOptionsData',
      options: () => {
        const { isAuthenticated, tenant } = props;
        return !isAuthenticated ? { variables: { tenantId: tenant._id } } : {};
      },
    }),
    // eslint-disable-next-line react/prop-types
    graphql(props.query, {
      options: ({ location }) => {
        const { query, isAuthenticated, tenant } = props;
        const { definitions } = query;
        const [firstDefinition] = definitions;
        const {
          name: { value: queryName },
        } = firstDefinition;
        const params = new URLSearchParams(location.search);
        const pageIndex = Math.max(0, Number(params.get('page')) - 1);
        const listingType = params.get('filter') || params.get('listingType');
        const validParam = (param) => {
          try {
            const jsonString = decodeURIComponent(param);
            const o = JSON.parse(jsonString);

            if (o && typeof o === 'object') {
              return o;
            }
          } catch (e) {
            return false;
          }
          return false;
        };

        let sector = [];
        if (validParam(params.get('sector'))) {
          sector = JSON.parse(decodeURIComponent(params.get('sector')));
        }

        let area = [];
        if (validParam(params.get('area'))) {
          area = JSON.parse(decodeURIComponent(params.get('area')));
        }

        const search = params.get('search');
        const searchRegex = search && {
          $regex: search.replace(/\s+/g, '|'),
          $options: 'ix',
        };
        const timing = params.get('timing');
        const typeOfOffer = params.get('typeOfOffer');
        const justLaunchedDate = moment().subtract(30, 'days').utc().format();

        let filter = {
          visibility: { $ne: 'UNPUBLISHED' },
          $or: [
            {
              approved: true,
            },
            {
              approved: false,
              status: 'PREMARKETING',
            },
          ],
          ...(timing === 'LAUNCHING_SOON' && { status: 'PREMARKETING' }),
          ...(timing === 'JUST_LAUNCHED' && {
            status: 'OPEN',
            openedAt: { $gte: justLaunchedDate, $ne: null },
          }),
        };

        if (queryName === 'allUserInvestments') {
          filter = {};
        }

        if (queryName === 'investorOffers') {
          filter = {
            $or: [
              {
                favorited: true,
              },
              {
                requestedInformation: true,
              },
              {
                readyToInvest: true,
              },
            ],
          };
        } else {
          filter = {
            ...filter,
            ...(listingType && { listingType }),
            ...(sector && sector.length && { sector }),
            ...(area && area.length && { area }),
            ...(typeOfOffer && typeOfOffer.length && { typeOfOffer }),
            ...(search && {
              $or: [
                { name: searchRegex }, // search in offer name
                { sector: searchRegex }, // search in offer sector
                { listingType: searchRegex }, // search in listing type
                { 'issuerCompany.name': searchRegex }, // search in company's name
              ],
            }),
          };
        }

        const skip = pageIndex * limit;
        const sort = 'order';
        const order = 'ASC';

        return {
          variables: {
            timing,
            typeOfOffer,
            search,
            query: {
              limit,
              skip,
              filter,
              sort,
              order,
            },
            ...(!isAuthenticated && { tenantId: tenant._id }),
          },
        };
      },
      props: ({ data, ownProps: { query, dispatch } }) => {
        // TODO: Create helper function to get first GraqphQL operation name
        const { definitions } = query;
        const [firstDefinition] = definitions;
        const {
          name: { value: queryName },
        } = firstDefinition;
        const {
          variables: {
            timing,
            typeOfOffer,
            search,
            query: { skip, filter },
          },
          loading,
          [queryName]: offerQuery,
        } = data;

        return {
          loading,
          queryName,
          offerQuery,
          pageIndex: skip / limit,
          filter,

          search,
          timing,
          typeOfOffer,
          changePage({ selected }) {
            const params = {
              page: selected + 1,
              filter: filter.listingType,
              sector: filter.sector,
              typeOfOffer,
              search,
              timing,
            };
            dispatch(
              push({
                search: `?${new URLSearchParams(pickBy(params)).toString()}`,
              })
            );
          },
        };
      },
    })
  )(ComponentToWrap);

  return <WithData {...props} />;
};

const mapStateToProps = ({ session, tenant }) => ({
  isAuthenticated: session.isAuthenticated,
  tenant,
});

export default connect(mapStateToProps)(withPagination(OfferPagination));



// WEBPACK FOOTER //
// ./src/components/Dashboard/OfferPagination.js